import React, { useState, useRef, useEffect } from 'react';

/**
 * OTP MARK USED - PART 4
 *
 * Invalidate codes after successful use
 * See EVERYTHING about preventing reuse
 */

interface MarkUsedEvent {
  id: string;
  timestamp: number;
  layer: 'query' | 'update' | 'index' | 'audit' | 'cache' | 'security';
  type: 'info' | 'success' | 'error' | 'process' | 'detail' | 'warning';
  message: string;
  details?: any;
  duration?: number;
}

interface CodeRecord {
  id: string;
  email: string;
  code: string;
  used: boolean;
  usedAt?: Date;
  usedBy?: string;
  attempts: number;
  createdAt: Date;
  expiresAt: Date;
}

export default function OTPMarkUsed() {
  const [events, setEvents] = useState<MarkUsedEvent[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedCode, setSelectedCode] = useState<CodeRecord | null>(null);
  const [showAuditLog, setShowAuditLog] = useState(false);

  // Simulated database with various states
  const [database, setDatabase] = useState<CodeRecord[]>([
    {
      id: 'otp-001',
      email: 'alice@example.com',
      code: '123456',
      used: false,
      attempts: 0,
      createdAt: new Date(Date.now() - 2 * 60 * 1000),
      expiresAt: new Date(Date.now() + 8 * 60 * 1000)
    },
    {
      id: 'otp-002',
      email: 'bob@example.com',
      code: '654321',
      used: false,
      attempts: 2,
      createdAt: new Date(Date.now() - 5 * 60 * 1000),
      expiresAt: new Date(Date.now() + 5 * 60 * 1000)
    },
    {
      id: 'otp-003',
      email: 'charlie@example.com',
      code: '999888',
      used: true,
      usedAt: new Date(Date.now() - 30 * 60 * 1000),
      usedBy: 'session-xyz',
      attempts: 1,
      createdAt: new Date(Date.now() - 35 * 60 * 1000),
      expiresAt: new Date(Date.now() - 25 * 60 * 1000)
    },
    {
      id: 'otp-004',
      email: 'david@example.com',
      code: '111222',
      used: false,
      attempts: 3,
      createdAt: new Date(Date.now() - 1 * 60 * 1000),
      expiresAt: new Date(Date.now() + 9 * 60 * 1000)
    }
  ]);

  const [auditLog, setAuditLog] = useState<any[]>([]);
  const eventsEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    eventsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [events]);

  const logEvent = (event: Omit<MarkUsedEvent, 'id' | 'timestamp'>) => {
    const newEvent: MarkUsedEvent = {
      ...event,
      id: `evt-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now()
    };

    setEvents(prev => [...prev, newEvent]);
    return new Promise(resolve => setTimeout(resolve, event.duration || 50));
  };

  const markAsUsed = async (record: CodeRecord) => {
    setIsProcessing(true);
    setEvents([]);
    setSelectedCode(record);
    const startTime = Date.now();
    const sessionId = `session-${Math.random().toString(36).substr(2, 9)}`;

    try {
      // Step 1: Query current state
      await logEvent({
        layer: 'query',
        type: 'info',
        message: 'Fetching current record state',
        details: {
          sql: `SELECT * FROM otp_codes WHERE id = ? FOR UPDATE`,
          recordId: record.id,
          lockType: 'PESSIMISTIC',
          purpose: 'Prevent race conditions'
        },
        duration: 75
      });

      // Step 2: Validate state
      await logEvent({
        layer: 'security',
        type: 'process',
        message: 'Validating record state',
        details: {
          currentState: {
            used: record.used,
            attempts: record.attempts,
            expired: record.expiresAt < new Date()
          },
          checks: [
            { test: 'not_already_used', passed: !record.used },
            { test: 'not_expired', passed: record.expiresAt > new Date() },
            { test: 'attempts_ok', passed: record.attempts < 5 }
          ]
        }
      });

      if (record.used) {
        await logEvent({
          layer: 'security',
          type: 'warning',
          message: 'Code already marked as used',
          details: {
            usedAt: record.usedAt,
            usedBy: record.usedBy,
            action: 'REJECT_DUPLICATE'
          }
        });
        return;
      }

      // Step 3: Prepare update
      const updateData = {
        used: true,
        usedAt: new Date(),
        usedBy: sessionId,
        attempts: record.attempts + 1
      };

      await logEvent({
        layer: 'update',
        type: 'process',
        message: 'Preparing database update',
        details: {
          sql: `UPDATE otp_codes SET used = ?, used_at = ?, used_by = ?, attempts = ? WHERE id = ?`,
          bindings: [true, updateData.usedAt.toISOString(), sessionId, updateData.attempts, record.id],
          changes: {
            before: { used: false, usedAt: null },
            after: { used: true, usedAt: updateData.usedAt.toISOString() }
          }
        },
        duration: 100
      });

      // Step 4: Execute update
      await logEvent({
        layer: 'update',
        type: 'process',
        message: 'Executing database update',
        details: {
          table: 'otp_codes',
          operation: 'UPDATE',
          affectedRows: 1,
          writeLocation: 'PRIMARY',
          replication: 'ASYNC'
        },
        duration: 150
      });

      // Update local state
      setDatabase(prev => prev.map(r =>
        r.id === record.id
          ? { ...r, ...updateData }
          : r
      ));

      // Step 5: Update indexes
      await logEvent({
        layer: 'index',
        type: 'detail',
        message: 'Updating database indexes',
        details: {
          indexesUpdated: [
            'idx_otp_used',
            'idx_otp_used_at',
            'idx_otp_session'
          ],
          cost: '3 index writes'
        }
      });

      // Step 6: Audit logging
      const auditEntry = {
        id: `audit-${Date.now()}`,
        action: 'MARK_USED',
        recordId: record.id,
        email: record.email,
        code: record.code.substring(0, 2) + '****',
        sessionId,
        timestamp: new Date(),
        ipAddress: '192.168.1.1',
        userAgent: 'Mozilla/5.0...'
      };

      await logEvent({
        layer: 'audit',
        type: 'info',
        message: 'Creating audit log entry',
        details: auditEntry,
        duration: 50
      });

      setAuditLog(prev => [...prev, auditEntry]);

      // Step 7: Cache invalidation
      await logEvent({
        layer: 'cache',
        type: 'process',
        message: 'Invalidating cache entries',
        details: {
          keys: [
            `otp:${record.email}:*`,
            `otp:active:${record.email}`,
            `session:otp:${sessionId}`
          ],
          cacheServers: ['cache-1', 'cache-2'],
          method: 'DELETE'
        }
      });

      // Step 8: Success
      await logEvent({
        layer: 'security',
        type: 'success',
        message: '✅ CODE MARKED AS USED',
        details: {
          recordId: record.id,
          email: record.email,
          usedAt: updateData.usedAt.toISOString(),
          sessionId,
          processingTime: `${Date.now() - startTime}ms`,
          nextAction: 'Proceed with authentication'
        }
      });

      // Step 9: Post-update verification
      await logEvent({
        layer: 'query',
        type: 'info',
        message: 'Verifying update success',
        details: {
          sql: `SELECT used, used_at FROM otp_codes WHERE id = ?`,
          result: {
            used: true,
            usedAt: updateData.usedAt.toISOString()
          },
          verified: true
        }
      });

    } catch (error) {
      await logEvent({
        layer: 'update',
        type: 'error',
        message: 'Failed to mark as used',
        details: { error: error instanceof Error ? error.message : 'Unknown error' }
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const simulateConcurrentAttempts = async () => {
    setEvents([]);
    const code = database.find(r => !r.used && r.attempts < 3);

    if (!code) {
      await logEvent({
        layer: 'security',
        type: 'warning',
        message: 'No available codes for concurrent test',
        details: { reason: 'All codes used or exceeded attempts' }
      });
      return;
    }

    await logEvent({
      layer: 'security',
      type: 'info',
      message: 'Simulating concurrent mark-used attempts',
      details: {
        codeId: code.id,
        simulatedSessions: 3
      }
    });

    // Simulate 3 concurrent attempts
    for (let i = 0; i < 3; i++) {
      await logEvent({
        layer: 'security',
        type: 'process',
        message: `Concurrent attempt ${i + 1}`,
        details: {
          sessionId: `concurrent-${i + 1}`,
          timestamp: Date.now(),
          result: i === 0 ? 'SUCCESS' : 'REJECTED_ALREADY_USED'
        },
        duration: 50
      });
    }

    await logEvent({
      layer: 'security',
      type: 'success',
      message: 'Race condition prevented',
      details: {
        winner: 'concurrent-1',
        rejected: ['concurrent-2', 'concurrent-3'],
        protection: 'Database row lock'
      }
    });
  };

  const cleanupUsedCodes = async () => {
    setEvents([]);
    const usedCodes = database.filter(r => r.used);

    await logEvent({
      layer: 'update',
      type: 'info',
      message: 'Starting cleanup of used codes',
      details: {
        totalCodes: database.length,
        usedCodes: usedCodes.length,
        policy: 'Delete after 24 hours'
      }
    });

    for (const code of usedCodes) {
      await logEvent({
        layer: 'update',
        type: 'detail',
        message: `Cleaning code ${code.id}`,
        details: {
          email: code.email,
          usedAt: code.usedAt,
          age: code.usedAt ? `${Math.floor((Date.now() - code.usedAt.getTime()) / 1000 / 60)} minutes` : 'N/A'
        },
        duration: 30
      });
    }

    await logEvent({
      layer: 'update',
      type: 'success',
      message: `Cleanup complete`,
      details: {
        deleted: usedCodes.length,
        freedSpace: `~${usedCodes.length * 256} bytes`
      }
    });
  };

  const getLayerColor = (layer: string) => {
    const colors: Record<string, string> = {
      query: 'text-blue-400',
      update: 'text-yellow-400',
      index: 'text-purple-400',
      audit: 'text-cyan-400',
      cache: 'text-orange-400',
      security: 'text-green-400'
    };
    return colors[layer] || 'text-gray-400';
  };

  const getLayerIcon = (layer: string) => {
    const icons: Record<string, string> = {
      query: '🔍',
      update: '✏️',
      index: '📇',
      audit: '📋',
      cache: '💾',
      security: '🔒'
    };
    return icons[layer] || '•';
  };

  const getRecordStatus = (record: CodeRecord) => {
    if (record.used) return { color: 'border-gray-600 opacity-50', status: 'USED' };
    if (record.expiresAt < new Date()) return { color: 'border-red-600', status: 'EXPIRED' };
    if (record.attempts >= 3) return { color: 'border-orange-600', status: 'LOCKED' };
    return { color: 'border-green-600', status: 'ACTIVE' };
  };

  return (
    <div className="min-h-screen bg-black text-green-400 font-mono">
      <div className="max-w-7xl mx-auto p-4">
        {/* Header */}
        <div className="mb-6 border border-green-400 p-4">
          <h1 className="text-3xl mb-2">🚫 OTP MARK USED - PART 4</h1>
          <p className="text-gray-400">Invalidate codes after successful use</p>
          <p className="text-yellow-400 text-sm mt-2">Prevent reuse with atomic operations</p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* Left: Database and Controls */}
          <div className="space-y-4">
            {/* Database State */}
            <div className="border border-green-400 p-4">
              <h2 className="text-xl mb-4 text-green-400">🗄️ DATABASE CODES</h2>

              <div className="space-y-2">
                {database.map(record => {
                  const { color, status } = getRecordStatus(record);
                  return (
                    <div
                      key={record.id}
                      className={`border p-3 ${color} cursor-pointer hover:opacity-80`}
                      onClick={() => !record.used && markAsUsed(record)}
                    >
                      <div className="flex justify-between mb-2">
                        <span className="text-sm">{record.id}</span>
                        <span className={`text-xs font-bold ${
                          status === 'ACTIVE' ? 'text-green-400' :
                          status === 'USED' ? 'text-gray-400' :
                          status === 'EXPIRED' ? 'text-red-400' :
                          'text-orange-400'
                        }`}>
                          {status}
                        </span>
                      </div>
                      <div className="text-xs space-y-1">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Email:</span>
                          <span>{record.email}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Code:</span>
                          <span className="font-bold">{record.code}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Attempts:</span>
                          <span>{record.attempts}/3</span>
                        </div>
                        {record.used && (
                          <>
                            <div className="flex justify-between">
                              <span className="text-gray-500">Used at:</span>
                              <span>{record.usedAt?.toLocaleTimeString()}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">Session:</span>
                              <span className="text-xs">{record.usedBy}</span>
                            </div>
                          </>
                        )}
                        <div className="flex justify-between">
                          <span className="text-gray-500">Expires:</span>
                          <span className={record.expiresAt < new Date() ? 'text-red-400' : ''}>
                            {record.expiresAt.toLocaleTimeString()}
                          </span>
                        </div>
                      </div>
                      {!record.used && status === 'ACTIVE' && (
                        <div className="mt-2 text-center text-yellow-400 text-xs">
                          Click to mark as used
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Controls */}
            <div className="border border-green-400 p-4">
              <h2 className="text-xl mb-4">🎮 CONTROLS</h2>

              <div className="space-y-2">
                <button
                  onClick={simulateConcurrentAttempts}
                  disabled={isProcessing}
                  className="w-full bg-blue-400 text-black p-3 hover:bg-blue-300 disabled:opacity-50"
                >
                  ⚡ SIMULATE RACE CONDITION
                </button>

                <button
                  onClick={cleanupUsedCodes}
                  disabled={isProcessing}
                  className="w-full bg-yellow-400 text-black p-3 hover:bg-yellow-300 disabled:opacity-50"
                >
                  🧹 CLEANUP USED CODES
                </button>

                <button
                  onClick={() => setShowAuditLog(!showAuditLog)}
                  className="w-full bg-purple-400 text-black p-3 hover:bg-purple-300"
                >
                  {showAuditLog ? '📊 HIDE AUDIT LOG' : '📊 SHOW AUDIT LOG'}
                </button>

                <button
                  onClick={() => {
                    setEvents([]);
                    setSelectedCode(null);
                  }}
                  className="w-full bg-gray-700 text-white p-3 hover:bg-gray-600"
                >
                  🔄 CLEAR EVENTS
                </button>
              </div>
            </div>

            {/* Audit Log */}
            {showAuditLog && (
              <div className="border border-purple-400 p-4">
                <h2 className="text-xl mb-4 text-purple-400">📋 AUDIT LOG</h2>

                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {auditLog.length === 0 ? (
                    <div className="text-gray-600 text-center py-4">No audit entries yet</div>
                  ) : (
                    auditLog.map((entry, i) => (
                      <div key={entry.id} className="border border-purple-600 p-2 text-xs">
                        <div className="flex justify-between">
                          <span className="text-purple-400">#{auditLog.length - i}</span>
                          <span className="text-gray-400">{entry.timestamp.toLocaleTimeString()}</span>
                        </div>
                        <div className="mt-1 space-y-1 text-gray-400">
                          <div>Action: {entry.action}</div>
                          <div>Email: {entry.email}</div>
                          <div>Code: {entry.code}</div>
                          <div>Session: {entry.sessionId}</div>
                          <div>IP: {entry.ipAddress}</div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Security Info */}
            <div className="border border-yellow-400 p-4">
              <h2 className="text-xl mb-4 text-yellow-400">🔒 SECURITY FEATURES</h2>
              <div className="text-xs text-gray-400 space-y-1">
                <div>• Pessimistic row locking</div>
                <div>• Atomic update operations</div>
                <div>• Race condition prevention</div>
                <div>• Complete audit trail</div>
                <div>• Cache invalidation</div>
                <div>• Session tracking</div>
              </div>
            </div>
          </div>

          {/* Right: Event Stream */}
          <div className="border border-green-400 p-4">
            <h2 className="text-xl mb-4">📡 MARK-USED EVENT STREAM</h2>

            <div className="h-[700px] overflow-y-auto space-y-2 text-xs font-mono">
              {events.length === 0 ? (
                <div className="text-gray-600 text-center py-8">
                  Click on an active code to mark it as used...
                </div>
              ) : (
                events.map(event => (
                  <div
                    key={event.id}
                    className={`border-l-2 pl-2 ${
                      event.type === 'success' ? 'border-green-400' :
                      event.type === 'error' ? 'border-red-400' :
                      event.type === 'warning' ? 'border-orange-400' :
                      event.type === 'process' ? 'border-blue-400' :
                      event.type === 'detail' ? 'border-purple-400' :
                      'border-gray-400'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-lg">{getLayerIcon(event.layer)}</span>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">
                            [{new Date(event.timestamp).toLocaleTimeString('en-US', {
                              hour12: false,
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit',
                              fractionalSecondDigits: 3
                            })}]
                          </span>
                          <span className={`font-bold ${getLayerColor(event.layer)}`}>
                            {event.layer.toUpperCase()}
                          </span>
                        </div>
                        <div className="text-gray-300 mt-1">{event.message}</div>
                        {event.details && (
                          <pre className="text-gray-500 mt-2 bg-gray-900 p-2 rounded overflow-x-auto">
{JSON.stringify(event.details, null, 2)}
                          </pre>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
              <div ref={eventsEndRef} />
            </div>
          </div>
        </div>

        {/* Bottom: Complete Picture */}
        {selectedCode && (
          <div className="mt-6 border border-cyan-400 p-4">
            <h2 className="text-xl text-cyan-400 mb-2">🎯 MARK-USED COMPLETE!</h2>
            <p className="text-gray-400 mb-4">
              Code {selectedCode.code} for {selectedCode.email} has been atomically marked as used.
              This prevents any future verification attempts and maintains security integrity.
            </p>
            <div className="grid grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-blue-400 font-bold">Query Layer:</span>
                <div className="text-gray-400">
                  • Row locking<br />
                  • State validation<br />
                  • Read verification
                </div>
              </div>
              <div>
                <span className="text-yellow-400 font-bold">Update Layer:</span>
                <div className="text-gray-400">
                  • Atomic operation<br />
                  • Timestamp recording<br />
                  • Session tracking
                </div>
              </div>
              <div>
                <span className="text-purple-400 font-bold">Audit Layer:</span>
                <div className="text-gray-400">
                  • Complete trail<br />
                  • IP tracking<br />
                  • Compliance ready
                </div>
              </div>
              <div>
                <span className="text-orange-400 font-bold">Cache Layer:</span>
                <div className="text-gray-400">
                  • Key invalidation<br />
                  • Multi-server<br />
                  • Session cleanup
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}